import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ro from 'date-fns/locale/ro';
import { api } from '@services/authService';

const DoctorSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [macroDialogOpen, setMacroDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', specialty: 'general', content: '' });
  const [newMacro, setNewMacro] = useState({ shortcut: '', text: '', description: '' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/doctor/settings');
      setSettings(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea setarilor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.patch('/doctor/settings', settings);
      alert('Setari salvate cu succes!');
    } catch (error) {
      console.error('Eroare la salvarea setarilor:', error);
      alert('Eroare la salvarea setarilor');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.name || !newTemplate.content) {
      alert('Completeaza toate campurile obligatorii');
      return;
    }

    try {
      const response = await api.post('/doctor/settings/templates', newTemplate);
      const updatedSettings = { ...settings };
      updatedSettings.note_templates = [...(updatedSettings.note_templates || []), response.data.data];
      setSettings(updatedSettings);
      setTemplateDialogOpen(false);
      setNewTemplate({ name: '', specialty: 'general', content: '' });
    } catch (error) {
      console.error('Eroare la adaugarea template-ului:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Sigur stergi acest template?')) return;

    try {
      await api.delete(`/doctor/settings/templates/${templateId}`);
      const updatedSettings = { ...settings };
      updatedSettings.note_templates = updatedSettings.note_templates.filter((t: any) => t.id !== templateId);
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Eroare la stergerea template-ului:', error);
    }
  };

  const handleAddMacro = async () => {
    if (!newMacro.shortcut || !newMacro.text) {
      alert('Completeaza toate campurile obligatorii');
      return;
    }

    try {
      const response = await api.post('/doctor/settings/macros', newMacro);
      const updatedSettings = { ...settings };
      updatedSettings.text_macros = { ...(updatedSettings.text_macros || {}), [newMacro.shortcut]: response.data.data };
      setSettings(updatedSettings);
      setMacroDialogOpen(false);
      setNewMacro({ shortcut: '', text: '', description: '' });
    } catch (error) {
      console.error('Eroare la adaugarea macro-ului:', error);
    }
  };

  const handleDeleteMacro = async (shortcut: string) => {
    if (!window.confirm('Sigur stergi acest macro?')) return;

    try {
      await api.delete(`/doctor/settings/macros/${shortcut}`);
      const updatedSettings = { ...settings };
      delete updatedSettings.text_macros[shortcut];
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Eroare la stergerea macro-ului:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Eroare la incarcarea setarilor</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Setari Doctor</Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Se salveaza...' : 'Salveaza Modificarile'}
          </Button>
        </Box>

        <Stack spacing={3}>
          {/* Informatii Profesionale */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informatii Profesionale
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Specialitate"
                    value={settings.specialty || ''}
                    onChange={(e) => setSettings({ ...settings, specialty: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Numar Licenta"
                    value={settings.license_number || ''}
                    onChange={(e) => setSettings({ ...settings, license_number: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Tip Angajare</InputLabel>
                    <Select
                      value={settings.employment_type || 'titular'}
                      onChange={(e) => setSettings({ ...settings, employment_type: e.target.value })}
                      label="Tip Angajare"
                    >
                      <MenuItem value="titular">Titular</MenuItem>
                      <MenuItem value="colaborator">Colaborator</MenuItem>
                      <MenuItem value="rezident">Rezident</MenuItem>
                      <MenuItem value="intern">Intern</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Durata Default Consultatie (minute)"
                    value={settings.default_slot_length || 30}
                    onChange={(e) => setSettings({ ...settings, default_slot_length: parseInt(e.target.value) })}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Preferinte Consultatii */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preferinte Consultatii
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.accepts_telemed || false}
                      onChange={(e) => setSettings({ ...settings, accepts_telemed: e.target.checked })}
                    />
                  }
                  label="Accepta Telemedicina"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.accepts_emergencies || false}
                      onChange={(e) => setSettings({ ...settings, accepts_emergencies: e.target.checked })}
                    />
                  }
                  label="Accepta Urgente"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Template-uri Note */}
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Template-uri Note Consultatii</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setTemplateDialogOpen(true)}
                >
                  Adauga Template
                </Button>
              </Box>

              {!settings.note_templates || settings.note_templates.length === 0 ? (
                <Alert severity="info">Nu ai template-uri salvate</Alert>
              ) : (
                <List>
                  {settings.note_templates.map((template: any) => (
                    <ListItem key={template.id}>
                      <ListItemText
                        primary={template.name}
                        secondary={`${template.specialty} • ${template.content.substring(0, 100)}...`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleDeleteTemplate(template.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Macros Text */}
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Macros Text (Scriere Rapida)</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setMacroDialogOpen(true)}
                >
                  Adauga Macro
                </Button>
              </Box>

              {!settings.text_macros || Object.keys(settings.text_macros).length === 0 ? (
                <Alert severity="info">Nu ai macros salvate</Alert>
              ) : (
                <List>
                  {Object.entries(settings.text_macros).map(([shortcut, data]: [string, any]) => (
                    <ListItem key={shortcut}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip label={shortcut} size="small" color="primary" />
                            <Typography variant="body2">{data.description || 'Fara descriere'}</Typography>
                          </Box>
                        }
                        secondary={data.text}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleDeleteMacro(shortcut)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Preferinte Notificari */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preferinte Notificari
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notification_preferences?.email || false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notification_preferences: {
                            ...settings.notification_preferences,
                            email: e.target.checked
                          }
                        })
                      }
                    />
                  }
                  label="Notificari Email"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notification_preferences?.push || false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notification_preferences: {
                            ...settings.notification_preferences,
                            push: e.target.checked
                          }
                        })
                      }
                    />
                  }
                  label="Notificari Push"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notification_preferences?.appointment_reminders || false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notification_preferences: {
                            ...settings.notification_preferences,
                            appointment_reminders: e.target.checked
                          }
                        })
                      }
                    />
                  }
                  label="Reminder Programari"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notification_preferences?.lab_results || false}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          notification_preferences: {
                            ...settings.notification_preferences,
                            lab_results: e.target.checked
                          }
                        })
                      }
                    />
                  }
                  label="Rezultate Lab"
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Template Dialog */}
        <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Template Nou</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nume Template"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Specialitate</InputLabel>
                <Select
                  value={newTemplate.specialty}
                  onChange={(e) => setNewTemplate({ ...newTemplate, specialty: e.target.value })}
                  label="Specialitate"
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="surgery">Chirurgie</MenuItem>
                  <MenuItem value="dermatology">Dermatologie</MenuItem>
                  <MenuItem value="cardiology">Cardiologie</MenuItem>
                  <MenuItem value="neurology">Neurologie</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Continut Template"
                value={newTemplate.content}
                onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                placeholder="Ex: Anamneza: [describe]&#10;Examen clinic: [findings]&#10;Diagnostic: [diagnosis]&#10;Plan tratament: [plan]"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialogOpen(false)}>Anuleaza</Button>
            <Button variant="contained" onClick={handleAddTemplate}>
              Adauga
            </Button>
          </DialogActions>
        </Dialog>

        {/* Macro Dialog */}
        <Dialog open={macroDialogOpen} onClose={() => setMacroDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Macro Nou</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Shortcut (ex: /vac, /rx)"
                value={newMacro.shortcut}
                onChange={(e) => setNewMacro({ ...newMacro, shortcut: e.target.value })}
                helperText="Scrie acest shortcut in note pentru auto-completare"
              />
              <TextField
                fullWidth
                label="Descriere"
                value={newMacro.description}
                onChange={(e) => setNewMacro({ ...newMacro, description: e.target.value })}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Text"
                value={newMacro.text}
                onChange={(e) => setNewMacro({ ...newMacro, text: e.target.value })}
                placeholder="Textul care va fi inserat cand folosesti shortcut-ul"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMacroDialogOpen(false)}>Anuleaza</Button>
            <Button variant="contained" onClick={handleAddMacro}>
              Adauga
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DoctorSettings;

